import { CalendarDate, today } from '@internationalized/date';
import { ComponentProps, ReactNode, useRef, useState } from 'react';

import guessUserTimeZone from '../../helpers/date/guessUserTimeZone';
import Card from '../Card/Card';
import DateCalendar from '../DateCalendar/DateCalendar';
import DialogModal from '../DialogModal/DialogModal';
import { ScrollHandles } from '../HorizontalSlider/HorizontalSlider';
import NearestDatesPicker from '../NearestDatesPicker/NearestDatesPicker';

type NearestDatePickerWithOthersProps = {
    moreLabel: ReactNode;
    defaultStart?: CalendarDate;
    minimum?: CalendarDate;
    onModalOpen?: () => void;
    onModalClose?: () => void;
} & Omit<
    ComponentProps<typeof NearestDatesPicker>,
    'startDate' | 'inline' | 'onMoreDatesRequested'
>;

const NearestDatePickerWithOthers = ({
    defaultStart,
    minimum,
    defaultValue,
    value,
    moreLabel,
    onModalOpen,
    onModalClose,
    onChange,
    ...nearestDatePickerProps
}: NearestDatePickerWithOthersProps) => {
    const timeZone = guessUserTimeZone();
    const [showingOthers, setShowingOthers] = useState(false);
    const scrollRef = useRef<ScrollHandles>(null);

    /**
     * Keep track of the current value as we need to communicate between
     * the NearestDatePicker and the regular one.
     */
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const valueToUse = value ?? uncontrolledValue;

    /**
     * The start date is the visual start date. It looks nicer when the
     * selected date is somewhere in the middle.
     */
    const [start, setStart] = useState<CalendarDate>(() => {
        const centerDate = value || defaultValue;
        if (centerDate) {
            return centerDate.subtract({ days: 2 });
        }

        return defaultStart ?? today(timeZone);
    });

    const handleMoreDatesRequested = () => {
        setShowingOthers(true);

        onModalOpen && onModalOpen();
    };

    const handleCloseModal = () => {
        setShowingOthers(false);

        onModalClose && onModalClose();
    };

    const handleDatePicked = (date?: CalendarDate) => {
        setUncontrolledValue(date);

        onChange && onChange(date);
    };

    const handleOtherDatePicked = (date: CalendarDate) => {
        setStart(date.subtract({ days: 2 }));

        scrollRef.current?.scrollTo(0);
        handleDatePicked(date);
        handleCloseModal();
    };

    return (
        <>
            <NearestDatesPicker
                {...nearestDatePickerProps}
                startDate={start}
                value={valueToUse}
                onMoreDatesRequested={handleMoreDatesRequested}
                onChange={handleDatePicked}
                moreLabel={moreLabel}
                minimum={minimum}
                scrollRef={scrollRef}
            />

            <DialogModal
                isOpen={showingOthers}
                width="small"
                backdrop="none"
                hideCloseButton
                unstyled
                onCloseRequested={handleCloseModal}
            >
                <Card horizontalPadding="small" verticalPadding="small">
                    <DateCalendar
                        value={valueToUse}
                        minValue={minimum}
                        onChange={handleOtherDatePicked}
                        preventHeightChange
                    />
                </Card>
            </DialogModal>
        </>
    );
};

export default NearestDatePickerWithOthers;
